import type { FormSelectOption } from '@practium/ui';
import type { CorrectionFormData } from '../types';

type CorrectionFormField = keyof CorrectionFormData;

interface SectorRecord {
	_id: string;
	name: string;
}

interface CorrectionRelationshipOptions {
	properties: FormSelectOption[];
	areas: FormSelectOption[];
	soilAnalyses: FormSelectOption[];
	sectors: FormSelectOption[];
}

export const getCorrectionReferenceId = (value: unknown): string => {
	if (typeof value === 'string') return value;
	if (!value || typeof value !== 'object') return '';

	const reference = value as { _id?: unknown; id?: unknown };
	const id = reference._id ?? reference.id;
	if (typeof id === 'string') return id;
	if (id == null) return '';
	const asString = String(id);
	return asString && asString !== '[object Object]' ? asString : '';
};

export const updateCorrectionFormField = <K extends CorrectionFormField>(
	form: CorrectionFormData,
	key: K,
	value: CorrectionFormData[K]
): CorrectionFormData => {
	switch (key) {
		case 'prop':
			return {
				...form,
				prop: value as string,
				area: '',
				soilAnalysis: '',
				sectors: [],
			};
		case 'area':
			return {
				...form,
				area: value as string,
				soilAnalysis: '',
				sectors: [],
			};
		case 'soilAnalysis':
			return {
				...form,
				soilAnalysis: value as string,
			};
		default:
			return { ...form, [key]: value };
	}
};

export const getCorrectionFieldsClearedByChange = (
	key: CorrectionFormField
): CorrectionFormField[] => {
	switch (key) {
		case 'prop':
			return ['prop', 'area', 'soilAnalysis', 'sectors'];
		case 'area':
			return ['area', 'soilAnalysis', 'sectors'];
		case 'soilAnalysis':
			return ['soilAnalysis'];
		default:
			return [key];
	}
};

export const buildCorrectionSectorOptions = (sectors: SectorRecord[]): FormSelectOption[] =>
	sectors.map((sector) => ({
		value: sector._id,
		label: sector.name,
	}));

export const validateCorrectionRelationships = (
	form: CorrectionFormData,
	options: CorrectionRelationshipOptions
): Record<string, string> => {
	const errors: Record<string, string> = {};
	const hasOption = (available: FormSelectOption[], value: string) =>
		available.some((option) => option.value === value);

	if (!hasOption(options.properties, form.prop)) {
		errors.prop = 'Selecione uma propriedade válida';
	}
	if (!hasOption(options.areas, form.area)) {
		errors.area = 'Selecione uma área vinculada à propriedade';
	}
	if (!hasOption(options.soilAnalyses, form.soilAnalysis)) {
		errors.soilAnalysis = 'Selecione uma análise de solo vinculada à área';
	}

	const validSectorIds = new Set(options.sectors.map((sector) => sector.value));
	if (form.sectors.some((sectorId) => !validSectorIds.has(sectorId))) {
		errors.sectors = 'Selecione um setor válido da área';
	}

	return errors;
};
